<template>
  <footer
    id="footer"
    data-test-id="footer"
    :class="[
      $style['footer'],
      { [$style['footer--inset-left']]: insetLeft },
      { [$style['footer--show']]: show }
    ]">
    <div
      :class="$style['footer__inner']"
      class="container container--inline container--small">
      <div :class="$style['footer__block']">
        <div class="h3 h-margin-bottom-s">
          {{ $t('footer/service') }}
        </div>
        <ul>
          <template v-for="item in serviceNavigation.filter(item => item.valid)" :key="item.id">
            <li>
              <NuxtLink :to="'/' + $lang + '/' + item.slug" :no-prefetch="true">
                {{ item.title }}
              </NuxtLink>
            </li>
            <li
              v-for="subitem in item.subItems"
              :key="subitem.id">
              <NuxtLink :to="'/' + $lang + '/' + subitem.slug" :no-prefetch="true">
                {{ subitem.title }}
              </NuxtLink>
            </li>
          </template>
        </ul>
      </div>
      <div :class="$style['footer__block']">
        <div class="h3 h-margin-bottom-s">
          {{ $t('footer/about') }}
        </div>
        <ul>
          <li
            v-for="item in aboutNavigation.filter(item => item.valid)"
            :key="item.id">
            <NuxtLink :to="'/' + $lang + '/' + item.slug" :no-prefetch="true">
              {{ item.title }}
            </NuxtLink>
          </li>
        </ul>
      </div>
      <div :class="$style['footer__block']">
        <div class="h3 h-margin-bottom-s">
          Social
        </div>
        <ul>
          <li>
            <a
              href="https://www.instagram.com/gutsgusto"
              rel="noopener"
              target="_blank">Instagram</a>
          </li>
          <li>
            <a
              href="https://www.facebook.com/gutsgusto/"
              rel="noopener"
              target="_blank">Facebook</a>
          </li>
          <li>
            <a
              href="https://www.youtube.com/gutsgustocom"
              rel="noopener"
              target="_blank">YouTube</a>
          </li>
          <li>
            <a
              href="https://www.tiktok.com/@gutsgusto"
              rel="noopener"
              target="_blank">TikTok</a>
          </li>
          <li>
            <a
              href="https://www.snapchat.com/add/gutsgusto"
              rel="noopener"
              target="_blank">Snapchat</a>
          </li>
          <li>
            <a
              href="https://pinterest.com/gutsgusto"
              rel="noopener"
              target="_blank">Pinterest</a>
          </li>
          <li>
            <a
              href="https://twitter.com/gutsgusto"
              rel="noopener"
              target="_blank">Twitter</a>
          </li>
        </ul>
      </div>
      <div :class="$style['footer__block']">
        <LazyImageList>
          <LazyImageListItem
            :image="'/logos/kiyoh.svg'"
            :width="40"
            :height="23"
            :href="`${$lang}-reviews`"
            :text="$t('footer/average-rating', { SCORE: 9.2 })"
            alt="Kiyoh" />
          <LazyImageListItem
            v-if="$lang === 'de'"
            :width="40"
            :height="40"
            :image="'/logos/trustmark_120x120.png'"
            :text="$t('footer/trust-1')"
            alt="Trusted shops" />
          <LazyImageListItem
            v-if="$lang === 'de'"
            :width="40"
            :height="40"
            :image="'/logos/hermes.png'"
            :text="$t('footer/trust-2')"
            alt="Hermes" />
          <LazyImageListItem
            v-if="$lang !== 'de'"
            :width="40"
            :height="40"
            :image="'/logos/postnl.svg'"
            :text="$t('footer/trust-2')"
            alt="PostNL" />
          <LazyImageListItem
            :width="40"
            :height="40"
            :image="'/logos/ssl.png'"
            :text="$t('footer/trust-3')"
            alt="SSL" />
        </LazyImageList>
      </div>
      <div :class="[$style['footer__block'], $style['footer__payment-logos']]">
        <LazyFooterPaymentMethods />
      </div>
      <div :class="($style['footer__block'], $style['footer__copyright'])">
        Built with ❤️&nbsp;&nbsp;for you - &copy;
        {{ new Date().getFullYear() }}<br><br>
        <span v-if="$lang === 'de'">
          <NuxtLink
            :no-prefetch="true"
            :to="{ name: 'de-colofon' }">Impressum</NuxtLink>&nbsp;-
        </span>
        <NuxtLink
          :no-prefetch="true"
          :to="{ name: $lang + '-privacy' }">
          {{ $t('footer/privacy') }}
        </NuxtLink>&nbsp;-
        <NuxtLink
          :no-prefetch="true"
          :to="{ name: $lang + '-terms' }">
          {{ $t('footer/terms') }}
        </NuxtLink>&nbsp;-
        <Btn
          no-padding="all"
          class="cky-banner-element"
          :class="$style['button']">
          {{ $t('footer/cookies') }}
        </Btn>
      </div>
    </div>
  </footer>
</template>
<script setup>

const { timedNavigation } = useTimedNavigation()

defineProps({
  insetLeft: {
    type: Boolean,
    default: false
  },
  show: {
    type: Boolean,
    default: true
  }
})

const serviceNavigation = computed(() => {
  return timedNavigation({ filterByLocation: 'service' })
})

const aboutNavigation = computed(() => {
  return timedNavigation({ filterByLocation: 'about' })
})

</script>
<style lang="scss" module>
.footer {
  background-color: var(--black);
  color: var(--white);
  display: none;
  font-size: var(--font-size);

  ul {
    @media (max-width: #{$breakpoint-mobile}) {
      column-count: 2;
    }

    li {
      color: var(--white);
      margin-bottom: var(--spacing);
      margin-left: 0;
      padding-left: 0;
      list-style-type: none;
    }
  }

  &__inner {
    display: grid;
    grid-row-gap: calc(var(--spacing ) * 6);
    grid-template-columns: 25% 25% 25% 25%;

    @media (max-width: #{$breakpoint-mobile}) {
      grid-template-columns: 100%;
      grid-row-gap: calc(var(--spacing ) * 3);
    }
  }

  &__block {
    border-bottom: 1px solid var(--grey-darkest);
    padding-bottom: calc(var(--spacing ) * 3);

    @media (min-width: #{$breakpoint-mobile}) {
      border: 0;
      padding-bottom: calc(var(--spacing ) * 6);
    }
  }

  &__payment-logos {
    @include hideScrollbar;

    /* stylelint-disable no-duplicate-selectors */
    & {
      grid-column: 1 / 5;
      justify-self: center;
      max-width: 100%;
      overflow-x: auto;
    }

    @media (max-width: #{$breakpoint-mobile}) {
      grid-column: 1/1;
    }
  }

  &__copyright {
    color: var(--grey);
    grid-column: 1 / 5;
    justify-self: center;
    text-align: center;

    @media (max-width: #{$breakpoint-mobile}) {
      grid-column: 1/1;
    }
  }

  &--show {
    display: block;
  }

  &--inset-left {
    @media (min-width: #{$breakpoint-tablet}) {
      padding-left: var(--filters-width);
    }
  }

  .button,
  a {
    background-color: transparent;
    color: var(--white);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
